import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery } from '@tanstack/react-query';

import type {
  ActivityAutopilotEstimateRequest,
  ActivityAutopilotSuggestRequest,
} from '../types/activity-autopilot';

import { estimateActivityAutopilot, suggestActivityAutopilot } from './activity-autopilot';
import { useGHGOrganizationId } from './use-ghg-organization-id';


export const activityAutopilotKeys = {
  all: ['ghg-activity-autopilot'] as const,
  estimate: (organizationId: string, request: ActivityAutopilotEstimateRequest | null) =>
    [...activityAutopilotKeys.all, 'estimate', organizationId, request] as const,
};

export function useSuggestActivityAutopilot() {
  const { getToken } = useAuth();
  const organizationId = useGHGOrganizationId();

  return useMutation({
    mutationFn: async (data: ActivityAutopilotSuggestRequest) => {
      if (!organizationId) throw new Error('Organization not found');
      return suggestActivityAutopilot(getToken, organizationId, data);
    },
  });
}

export function useEstimateActivityAutopilot() {
  const { getToken } = useAuth();
  const organizationId = useGHGOrganizationId();

  return useMutation({
    mutationFn: async (data: ActivityAutopilotEstimateRequest) => {
      if (!organizationId) throw new Error('Organization not found');
      return estimateActivityAutopilot(getToken, organizationId, data);
    },
  });
}

export function useActivityAutopilotEstimate(
  request: ActivityAutopilotEstimateRequest | null,
  options?: { enabled?: boolean }
) {
  const { getToken, isSignedIn } = useAuth();
  const organizationId = useGHGOrganizationId();

  return useQuery({
    queryKey: activityAutopilotKeys.estimate(organizationId ?? '', request),
    queryFn: async () => {
      if (!organizationId || !request) throw new Error('Autopilot estimate request is required');
      return estimateActivityAutopilot(getToken, organizationId, request);
    },
    enabled: (isSignedIn ?? false) && !!organizationId && !!request && (options?.enabled ?? true),
    staleTime: 0,
  });
}
